import { CONFIG } from '../config';
import type { Bounds, BossType } from '../types';
import type { Bullet } from './Bullet';

/**
 * 보스 기본 추상 클래스
 */
export abstract class Boss {
    public x: number;
    public y: number;
    public radius: number;
    public health: number;
    public maxHealth: number;
    public isAlive: boolean;
    public bossType: BossType;
    public rotation: number;
    public bulletBuffer: Bullet[] = [];

    public canvasWidth: number;
    public canvasHeight: number;

    constructor(x: number, y: number, health: number, type: BossType, canvasWidth: number, canvasHeight: number) {
        this.x = x;
        this.y = y;
        this.radius = 30;
        this.health = health;
        this.maxHealth = health;
        this.isAlive = true;
        this.bossType = type;
        this.rotation = 0;
        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
    }

    /**
     * 보스 업데이트 (각 보스 타입별로 구현)
     */
    abstract update(deltaTime: number, playerX: number, playerY: number): void;

    /**
     * 보스 렌더링 (각 보스 타입별로 구현)
     */
    abstract render(ctx: CanvasRenderingContext2D): void;

    /**
     * 보스 이름 반환
     */
    abstract getName(): string;

    /**
     * 데미지 처리
     * @returns true면 사망
     */
    takeDamage(amount: number = 1): boolean {
        this.health -= amount;
        if (this.health <= 0) {
            this.health = 0;
            this.isAlive = false;
            return true;
        }
        return false;
    }

    /**
     * 충돌 영역 반환
     */
    getBounds(): Bounds {
        return {
            x: this.x,
            y: this.y,
            radius: this.radius
        };
    }

    /**
     * 생성된 총알 가져오기 (호출 후 버퍼 비움)
     */
    getBullets(): Bullet[] {
        const bullets = [...this.bulletBuffer];
        this.bulletBuffer = [];
        return bullets;
    }

    /**
     * 기본 보스 외형 렌더링 (공통)
     */
    protected renderBossCore(ctx: CanvasRenderingContext2D, color: string = '#FFFFFF'): void {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        // 외곽 원 (하얀색 라인)
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
        ctx.stroke();

        // 체력에 따른 내부 원 (라인만)
        const healthRatio = this.health / this.maxHealth;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(0, 0, this.radius * healthRatio, 0, Math.PI * 2);
        ctx.stroke();

        ctx.restore();
    }

    /**
     * 플레이 영역 내로 제한
     */
    protected clampToPlayArea(): void {
        const margin = CONFIG.PLAY_AREA.MARGIN + this.radius;
        this.x = Math.max(margin, Math.min(this.canvasWidth - margin, this.x));
        this.y = Math.max(margin, Math.min(this.canvasHeight - margin, this.y));
    }
}
